import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { authAPI, chatAPI } from '../services/api';
import webSocketService from '../services/websocket';
import MessageDisplay from './MessageDisplay';
import { 
  X,
  Search,
  Send,
  Users,
  Building2,
  MessageCircle,
  UserCheck,
  Loader2,
  Clock,
  Plus,
  Upload,
  Paperclip,
  Image,
  FileText,
  Smile,
  Trash2
} from 'lucide-react';

const SimpleChatSystem = ({ isOpen, onClose }) => {
  const { user, isAdmin, isEmployee, isCustomer, isDemo } = useAuth();
  const { toast } = useToast();
  
  // DEBUG: Log user object to see what's available
  console.log('DEBUG SimpleChatSystem user object:', user);
  
  // WebSocket connection state
  const [wsConnected, setWsConnected] = useState(false);
  
  // Main states
  const [activeCategory, setActiveCategory] = useState('customers'); // 'team' or 'customers'
  const [filterType, setFilterType] = useState('all'); // 'all', 'unread', 'unresponded'
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [teamContacts, setTeamContacts] = useState([]);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  
  // Mobile responsiveness
  const [showMobileSidebar, setShowMobileSidebar] = useState(true);
  
  // File upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roomToDelete, setRoomToDelete] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mobile: Default to conversation list for admin/employee
  useEffect(() => {
    if (isOpen && (isAdmin() || isEmployee())) {
      const isMobile = window.innerWidth < 768;
      if (isMobile && !selectedChat) {
        setShowMobileSidebar(true);
      }
    }
  }, [isOpen, isAdmin, isEmployee, selectedChat]);

  // Load contacts and rooms when chat opens
  useEffect(() => {
    if (!isOpen || !user) return;
    
    const loadData = async () => {
      try {
        setLoading(true);
        
        if (isCustomer() || isDemo()) {
          // For customers/demo users, load their support messages
          await loadCustomerSupportMessages();
        } else {
          // For admin/employees, load contacts and rooms
          await loadContacts();
          await loadRooms();
        }
        
      } catch (error) {
        console.error('Error loading chat data:', error);
        toast({
          title: "Error",
          description: "Failed to load chat data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [isOpen, user, filterType]);

  const loadContacts = async () => {
    try {
      const response = await authAPI.getAllUsers();
      const users = response.data.users || [];
      
      // Separate into team and customers
      const team = users.filter(u => u.role === 'admin' || u.role === 'employee');
      const customers = users.filter(u => u.role === 'customer' || u.role === 'demo');
      
      console.log('DEBUG loadContacts:', {
        totalUsers: users.length,
        teamMembers: team.length,
        customers: customers.length,
        teamMemberNames: team.map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email),
        currentUser: user?.email
      });
      
      setTeamContacts(team);
      setCustomerContacts(customers);
      
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const loadRooms = useCallback(async () => {
    try {
      const response = await chatAPI.getRooms(filterType);
      const roomsData = response.data || [];
      
      console.log('DEBUG loadRooms:', {
        filterType,
        totalRooms: roomsData.length,
        roomsData: roomsData.map(r => ({ 
          id: r.id, 
          name: r.name, 
          room_type: r.room_type, 
          participants: r.participants?.length || 0,
          participant_roles: r.participant_details?.map(p => p.role) || []
        }))
      });
      
      // Separate rooms by category
      const teamRooms = roomsData.filter(room => {
        // Team rooms are direct rooms between admin and employees, or between employees
        if (room.room_type !== 'direct') return false;
        
        const participantRoles = room.participant_details?.map(p => p.role) || [];
        const hasCustomerOrDemo = participantRoles.some(role => role === 'customer' || role === 'demo');
        const hasAdminOrEmployee = participantRoles.some(role => role === 'admin' || role === 'employee');
        
        // Team room should have admin/employee participants but no customers/demos
        return hasAdminOrEmployee && !hasCustomerOrDemo;
      });
      
      const customerRooms = roomsData.filter(room => {
        // Customer rooms are support rooms OR rooms with customer/demo participants
        if (room.room_type === 'support') return true;
        
        const participantRoles = room.participant_details?.map(p => p.role) || [];
        return participantRoles.some(role => role === 'customer' || role === 'demo');
      });
      
      console.log('DEBUG room categorization:', {
        totalRooms: roomsData.length,
        teamRooms: teamRooms.length,
        customerRooms: customerRooms.length,
        teamRoomNames: teamRooms.map(r => r.name),
        customerRoomNames: customerRooms.map(r => r.name)
      });
      
      // Update unread counts
      const counts = {};
      roomsData.forEach(room => {
        if (room.unread_count > 0) {
          counts[room.id] = room.unread_count;
        }
      });
      setUnreadCounts(counts);
      
      setRooms({ team: teamRooms, customers: customerRooms });
      
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  }, [filterType]);

  const handleDeleteRoom = async (room) => {
    setRoomToDelete(room);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteRoom = async () => {
    if (!roomToDelete) return;
    
    try {
      await chatAPI.deleteRoom(roomToDelete.id);
      
      // Close selected chat if it was the deleted room
      if (selectedChat?.id === roomToDelete.id) {
        setSelectedChat(null);
        setMessages([]);
      }
      
      // Refresh room list
      await loadRooms();
      
      toast({
        title: "Chat Deleted",
        description: `Chat "${roomToDelete.name}" has been deleted successfully`,
      });
      
    } catch (error) {
      console.error('Error deleting room:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete chat. Please try again.",
        variant: "destructive",
      });
    } finally {
      setShowDeleteConfirm(false);
      setRoomToDelete(null);
    }
  };

  const cancelDeleteRoom = () => {
    setShowDeleteConfirm(false);
    setRoomToDelete(null);
  };

  const loadCustomerSupportMessages = async () => {
    try {
      const roomsResponse = await chatAPI.getRooms('all');
      const rooms = roomsResponse.data || [];
      
      if (rooms.length > 0) {
        // Find or create support room
        let supportRoom = rooms.find(room => 
          room.room_type === 'support' || 
          room.participants?.some(p => p.role === 'admin' || p.role === 'employee')
        ) || rooms[0];
        
        if (supportRoom) {
          setSelectedChat({
            id: supportRoom.id,
            name: 'BrixBot Support',
            type: 'support'
          });
          
          // Load messages with correct room type
          await loadRoomMessages(supportRoom.id, 'support');
        }
      }
      
    } catch (error) {
      console.error('Error loading customer support messages:', error);
    }
  };

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log('DEBUG WebSocket message received:', data);
    
    if (data.type === 'new_message') {
      const messageRoomId = data.room_id;
      
      console.log('DEBUG WebSocket new_message:', {
        messageRoomId,
        selectedChatId: selectedChat?.id,
        isForCurrentChat: selectedChat?.id === messageRoomId
      });
      
      // Always refresh room list to update unread counts first
      loadRooms();
      
      // If this message is for the currently selected chat, add it to messages
      if (selectedChat?.id === messageRoomId) {
        // Get the room from our current rooms to determine correct type
        const currentRooms = [...(rooms.team || []), ...(rooms.customers || [])];
        const messageRoom = currentRooms.find(room => room.id === messageRoomId);
        const roomType = messageRoom?.room_type || selectedChat?.type || 'support';
        
        console.log('DEBUG WebSocket message processing:', {
          roomType,
          messageRoom: messageRoom?.id,
          selectedChatType: selectedChat?.type
        });
        
        const chatContext = { id: messageRoomId, type: roomType };
        const { isOwnMessage, displayName } = getMessageDisplayInfo(data.message, user, chatContext);
        
        const newMessage = {
          id: data.message.id,
          content: data.message.content,
          sender_id: data.message.sender_id,
          sender_name: displayName,
          sender_role: data.message.sender_role,
          created_at: data.message.created_at,
          attachment: data.message.attachment,
          message_type: data.message.message_type,
          isOwn: isOwnMessage
        };
        
        console.log('DEBUG WebSocket adding message to UI:', newMessage);
        
        setMessages(prev => {
          // Check if message already exists to prevent duplicates
          const exists = prev.find(msg => msg.id === newMessage.id);
          if (exists) {
            console.log('DEBUG WebSocket message already exists, skipping');
            return prev;
          }
          console.log('DEBUG WebSocket adding new message to messages list');
          return [...prev, newMessage];
        });
        
        // Mark as read if this is not our own message
        const userId = user?.user_id || user?.id || user?.sub;
        if (data.message.sender_id !== userId) {
          try {
            chatAPI.markMessagesRead(messageRoomId);
            console.log('DEBUG WebSocket marked messages as read for room:', messageRoomId);
          } catch (error) {
            console.error('Error marking WebSocket message as read:', error);
          }
        }
      } else {
        console.log('DEBUG WebSocket message not for current chat, only refreshing room list');
      }
    }
  }, [user, loadRooms, selectedChat, rooms]);

  // Polling fallback for when WebSocket fails
  useEffect(() => {
    if (!isOpen || !selectedChat?.id || wsConnected) return;
    
    console.log('DEBUG: WebSocket not connected, starting polling fallback');
    
    const pollForMessages = async () => {
      try {
        const response = await chatAPI.getMessages(selectedChat.id, 50, 0);
        const newMessages = response.data || [];
        
        // Check if we have new messages
        if (newMessages.length > messages.length) {
          console.log('DEBUG: Polling found new messages, updating UI');
          
          const chatContext = selectedChat;
          const updatedMessages = newMessages.map(msg => {
            const { isOwnMessage, displayName } = getMessageDisplayInfo(msg, user, chatContext);
            
            return {
              id: msg.id,
              content: msg.content,
              sender_id: msg.sender_id,
              sender_name: displayName,
              sender_role: msg.sender_role,
              created_at: msg.created_at,
              attachment: msg.attachment,
              message_type: msg.message_type,
              isOwn: isOwnMessage
            };
          });
          
          setMessages(updatedMessages);
          
          // Also refresh room list for unread counts
          loadRooms();
        }
      } catch (error) {
        console.error('Error polling for messages:', error);
      }
    };
    
    // Poll every 2 seconds when WebSocket is not connected
    const pollInterval = setInterval(pollForMessages, 2000);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [isOpen, selectedChat?.id, wsConnected, messages.length, user, loadRooms]);
  // Initialize WebSocket connection
  useEffect(() => {
    if (!isOpen || !user) return;
    
    // Try multiple token retrieval methods
    const token = localStorage.getItem('brixbot_token') || 
                  sessionStorage.getItem('brixbot_token') ||
                  localStorage.getItem('token') ||
                  sessionStorage.getItem('token');
    
    console.log('DEBUG WebSocket: Token search result:', {
      localStorage_brixbot: localStorage.getItem('brixbot_token') ? 'found' : 'not found',
      sessionStorage_brixbot: sessionStorage.getItem('brixbot_token') ? 'found' : 'not found',
      localStorage_token: localStorage.getItem('token') ? 'found' : 'not found',
      sessionStorage_token: sessionStorage.getItem('token') ? 'found' : 'not found',
      finalToken: token ? token.substring(0, 20) + '...' : 'NOT FOUND'
    });
    
    if (!token) {
      console.log('❌ DEBUG WebSocket: No token found in any storage location');
      setWsConnected(false);
      return;
    }
    
    console.log('✅ DEBUG WebSocket: Found token, attempting connection');
    
    // Connect to WebSocket
    webSocketService.connect(token).then(() => {
      console.log('✅ DEBUG WebSocket: Connected successfully for chat');
      setWsConnected(true);
      webSocketService.addMessageHandler(handleWebSocketMessage);
    }).catch(error => {
      console.error('❌ DEBUG WebSocket: Connection failed:', error);
      setWsConnected(false);
    });
    
    // Cleanup on component unmount or chat close
    return () => {
      console.log('DEBUG WebSocket: Cleaning up connection');
      webSocketService.removeMessageHandler(handleWebSocketMessage);
      setWsConnected(false);
      if (!isOpen) {
        webSocketService.disconnect();
      }
    };
  }, [isOpen, user, handleWebSocketMessage]); // Added handleWebSocketMessage to dependencies

  const getMessageDisplayInfo = (message, currentUser, selectedChat) => {
    // Get user ID using multiple field names that might exist
    const userId = currentUser?.user_id || currentUser?.id || currentUser?.sub;
    
    // Debug logging
    console.log('DEBUG getMessageDisplayInfo:', {
      message_sender_id: message.sender_id,
      message_sender_role: message.sender_role,
      current_user_object: currentUser,
      current_user_id: userId,
      current_user_role: currentUser?.role,
      selected_chat_type: selectedChat?.type,
      selected_chat_id: selectedChat?.id
    });
    
    if (!userId) {
      console.error('ERROR: User ID not found in currentUser object!', currentUser);
      return { isOwnMessage: false, displayName: message.sender_name || 'Unknown' };
    }
    
    // CUSTOMER/DEMO users logic - ALWAYS show their own messages on the right
    if (currentUser.role === 'customer' || currentUser.role === 'demo') {
      if (message.sender_id === userId) {
        console.log('✅ DEBUG: Customer own message - RIGHT SIDE');
        return { 
          isOwnMessage: true, 
          displayName: message.sender_name || currentUser.first_name || 'You' 
        };
      } else {
        console.log('DEBUG: Support message to customer - LEFT SIDE');
        return { 
          isOwnMessage: false, 
          displayName: (message.sender_role === 'admin' || message.sender_role === 'employee') 
            ? 'BrixBot Support' 
            : message.sender_name 
        };
      }
    }
    
    // For admin/employee users in support chats
    if (selectedChat?.type === 'support') {
      if (currentUser.role === 'employee') {
        // Employee POV: Their messages + admin messages on right, customer messages on left
        if (message.sender_role === 'employee' || message.sender_role === 'admin') {
          return { 
            isOwnMessage: true, 
            displayName: message.sender_role === 'admin' ? 'Admin' : message.sender_name 
          };
        } else {
          return { 
            isOwnMessage: false, 
            displayName: message.sender_name 
          };
        }
      } else if (currentUser.role === 'admin') {
        // Admin POV: Their messages + assigned employee messages on right, customer messages on left
        if (message.sender_role === 'admin' || message.sender_role === 'employee') {
          return { 
            isOwnMessage: true, 
            displayName: message.sender_role === 'admin' ? 'You' : message.sender_name 
          };
        } else {
          return { 
            isOwnMessage: false, 
            displayName: message.sender_name 
          };
        }
      }
    }
    
    // Default/Team chat logic - standard behavior
    const isOwnMessage = message.sender_id === userId;
    const displayName = isOwnMessage ? 'You' : message.sender_name;
    
    console.log('DEBUG getMessageDisplayInfo result:', { isOwnMessage, displayName });
    return { isOwnMessage, displayName };
  };

  const loadRoomMessages = async (roomId, roomType = null) => {
    try {
      const response = await chatAPI.getMessages(roomId, 50, 0);
      const roomMessages = response.data || [];
      
      setMessages(roomMessages.map(msg => {
        // Use passed roomType or fall back to selectedChat
        const chatContext = roomType ? { id: roomId, type: roomType } : selectedChat;
        const { isOwnMessage, displayName } = getMessageDisplayInfo(msg, user, chatContext);
        
        return {
          id: msg.id,
          content: msg.content,
          sender_id: msg.sender_id,
          sender_name: displayName,
          sender_role: msg.sender_role,
          created_at: msg.created_at,
          attachment: msg.attachment,
          message_type: msg.message_type,
          isOwn: isOwnMessage
        };
      }));
      
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const startChat = async (contact) => {
    try {
      setLoading(true);
      
      // Find existing room with this contact
      const allRooms = [...(rooms.team || []), ...(rooms.customers || [])];
      let existingRoom = allRooms.find(room => 
        room.participants && room.participants.some(p => p.id === contact.id)
      );
      
      if (!existingRoom) {
        // Create new room
        const roomData = {
          name: `Chat with ${contact.first_name} ${contact.last_name}`.trim() || contact.email,
          participants: [user.user_id, contact.id],
          room_type: contact.role === 'customer' || contact.role === 'demo' ? 'support' : 'direct'
        };
        
        const response = await chatAPI.createRoom(roomData);
        existingRoom = response.data;
      }
      
      setSelectedChat({
        id: existingRoom.id,
        name: `${contact.first_name} ${contact.last_name}`.trim() || contact.email,
        type: existingRoom.room_type,
        contact: contact
      });
      
      // Load messages for this room with correct type
      await loadRoomMessages(existingRoom.id, existingRoom.room_type);
      
      // Mark messages as read and refresh room data
      try {
        await chatAPI.markMessagesRead(existingRoom.id);
        
        // Refresh rooms to update unread counts
        await loadRooms();
        
        // Clear unread count for this room locally
        setUnreadCounts(prev => {
          const updated = { ...prev };
          delete updated[existingRoom.id];
          return updated;
        });
        
      } catch (readError) {
        console.error('Error marking messages as read:', readError);
      }
      
      // Close mobile sidebar when chat is selected
      if (window.innerWidth < 768) {
        setShowMobileSidebar(false);
      }
      
    } catch (error) {
      console.error('Error starting chat:', error);
      toast({
        title: "Error",
        description: "Failed to start chat",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select a file smaller than 10MB",
        variant: "destructive",
      });
      return;
    }

    // Use same validation as chatbot creator
    const allowedTypes = [
      'image/', 'application/pdf', 'application/msword', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel', 
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/', 'application/rtf'
    ];
    
    const isAllowed = allowedTypes.some(type => file.type.startsWith(type));
    if (!isAllowed) {
      toast({
        title: "Unsupported file type",
        description: `${file.name} is not a supported file type`,
        variant: "destructive",
      });
      return;
    }

    // Convert to base64 using same pattern as chatbot creator
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target.result.split(',')[1];
      setSelectedFile({
        id: Math.random().toString(36).substr(2, 9),
        filename: file.name,
        content_type: file.type,
        file_data: base64,
        file_size: file.size
      });
    };
    reader.readAsDataURL(file);
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const sendMessage = async () => {
    if (!selectedChat || (!newMessage.trim() && !selectedFile)) return;

    const messageContent = newMessage.trim() || (selectedFile ? `📎 ${selectedFile.filename}` : '');
    
    // Create optimistic message with proper display logic
    const tempMessage = {
      id: `temp_${Date.now()}`,
      content: messageContent,
      sender_id: user.user_id,
      sender_role: user.role,
      created_at: new Date().toISOString(),
      attachment: selectedFile,
      message_type: selectedFile ? (selectedFile.content_type.startsWith('image/') ? 'image' : 'file') : 'text',
    };
    
    const { isOwnMessage, displayName } = getMessageDisplayInfo(tempMessage, user, selectedChat);
    
    const optimisticMessage = {
      ...tempMessage,
      sender_name: displayName,
      isOwn: isOwnMessage,
      sending: true
    };

    // Add optimistic message
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');
    const fileToSend = selectedFile;
    setSelectedFile(null);

    try {
      setUploadingFile(true);
      
      const messageData = {
        content: messageContent,
        message_type: optimisticMessage.message_type
      };
      
      if (fileToSend) {
        messageData.attachment = fileToSend;
      }
      
      const response = await chatAPI.sendMessage(selectedChat.id, messageData);
      
      // Remove optimistic message and add real message with proper display info
      setMessages(prev => {
        const filtered = prev.filter(msg => msg.id !== optimisticMessage.id);
        
        const { isOwnMessage: realIsOwn, displayName: realDisplayName } = getMessageDisplayInfo(response.data, user, selectedChat);
        
        const realMessage = {
          id: response.data.id,
          content: response.data.content,
          sender_id: response.data.sender_id,
          sender_name: realDisplayName,
          sender_role: response.data.sender_role,
          created_at: response.data.created_at,
          attachment: response.data.attachment,
          message_type: response.data.message_type,
          isOwn: realIsOwn
        };
        
        return [...filtered, realMessage];
      });
      
      toast({
        title: "Message Sent",
        description: fileToSend ? "File sent successfully" : "Message sent successfully",
      });
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Remove failed message
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      
      // Restore content
      setNewMessage(messageContent);
      if (fileToSend) {
        setSelectedFile(fileToSend);
      }
      
      toast({
        title: "Send Failed",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileDownload = (attachment) => {
    try {
      if (!attachment || !attachment.file_data) {
        toast({
          title: "Download Failed",
          description: "File data not available",
          variant: "destructive",
        });
        return;
      }

      // Use same download pattern as chatbot creator
      const byteCharacters = atob(attachment.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      const blob = new Blob([byteArray], { type: attachment.content_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const fileName = attachment.filename || `download_${Date.now()}`;
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      
      document.body.appendChild(a);
      a.click();
      
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast({
        title: "Download Started",
        description: `Downloading ${fileName}`,
      });
      
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Download Failed",
        description: `Download error: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getCurrentRooms = () => {
    if (!rooms.team && !rooms.customers) return [];
    const currentRooms = activeCategory === 'team' ? (rooms.team || []) : (rooms.customers || []);
    
    // Apply filter
    let filteredRooms = currentRooms;
    if (filterType === 'unread') {
      filteredRooms = currentRooms.filter(room => room.unread_count > 0);
    } else if (filterType === 'no_reply') {
      filteredRooms = currentRooms.filter(room => !room.has_reply && room.last_message_sender !== user.user_id);
    }
    
    // Sort: unread first, then by last activity
    return filteredRooms.sort((a, b) => {
      if (a.unread_count && !b.unread_count) return -1;
      if (!a.unread_count && b.unread_count) return 1;
      return new Date(b.last_activity || b.created_at) - new Date(a.last_activity || a.created_at);
    });
  };

  const getAllAvailableContacts = () => {
    // Get current rooms to see who we already have rooms with
    const existingRooms = getCurrentRooms();
    const existingContactIds = new Set();
    
    existingRooms.forEach(room => {
      if (room.participants) {
        room.participants.forEach(p => {
          if (p.id !== user.user_id) {
            existingContactIds.add(p.id);
          }
        });
      }
    });
    
    // Get all contacts for current category
    const allContacts = activeCategory === 'team' ? teamContacts : customerContacts;
    
    // Filter by search query
    const searchFiltered = !searchQuery ? allContacts : allContacts.filter(contact => {
      const name = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
      const email = contact.email.toLowerCase();
      const company = contact.company_name?.toLowerCase() || '';
      
      return name.includes(searchQuery.toLowerCase()) || 
             email.includes(searchQuery.toLowerCase()) ||
             company.includes(searchQuery.toLowerCase());
    });
    
    // Return contacts who don't have existing rooms
    return searchFiltered.filter(contact => !existingContactIds.has(contact.id));
  };

  if (!isOpen) return null;

  // Customer/Demo view - Simple support chat
  if (isCustomer() || isDemo()) {
    const handleCustomerSendMessage = async () => {
      if (!newMessage.trim() && !selectedFile) return;

      const messageContent = newMessage.trim() || (selectedFile ? `📎 ${selectedFile.filename}` : '');
      const optimisticMessage = {
        id: `temp_${Date.now()}`,
        content: messageContent,
        sender_id: user.user_id,
        sender_name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        sender_role: user.role,
        created_at: new Date().toISOString(),
        attachment: selectedFile,
        message_type: selectedFile ? (selectedFile.content_type.startsWith('image/') ? 'image' : 'file') : 'text',
        isOwn: true,
        sending: true
      };

      // Add optimistic message
      setMessages(prev => [...prev, optimisticMessage]);
      setNewMessage('');
      const fileToSend = selectedFile;
      setSelectedFile(null);

      try {
        setUploadingFile(true);
        
        // Find or create support room
        let supportRoom;
        const roomsResponse = await chatAPI.getRooms('all');
        const rooms = roomsResponse.data || [];
        
        supportRoom = rooms.find(room => 
          room.room_type === 'support' || 
          room.participants?.some(p => p.role === 'admin' || p.role === 'employee')
        );
        
        if (!supportRoom) {
          // Create support room if it doesn't exist
          const roomData = {
            name: `Customer Support - ${user.first_name} ${user.last_name}`.trim() || user.email,
            participants: [user.user_id],
            room_type: 'support'
          };
          
          const newRoomResponse = await chatAPI.createRoom(roomData);
          supportRoom = newRoomResponse.data;
        }
        
        const messageData = {
          content: messageContent,
          message_type: optimisticMessage.message_type
        };
        
        if (fileToSend) {
          messageData.attachment = fileToSend;
        }
        
        await chatAPI.sendMessage(supportRoom.id, messageData);
        
        // Remove sending status
        setMessages(prev => prev.map(msg => 
          msg.id === optimisticMessage.id ? { ...msg, sending: false } : msg
        ));
        
        toast({
          title: "Message Sent",
          description: fileToSend ? "File sent to support successfully" : "Message sent to support successfully",
        });
        
      } catch (error) {
        console.error('Error sending customer message:', error);
        
        // Remove failed message
        setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
        
        // Restore content
        setNewMessage(messageContent);
        if (fileToSend) {
          setSelectedFile(fileToSend);
        }
        
        toast({
          title: "Send Failed",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      } finally {
        setUploadingFile(false);
      }
    };

    const handleCustomerKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleCustomerSendMessage();
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 md:p-4">
        <Card className="w-full max-w-4xl h-[600px] bg-gray-900 border-violet-500/20 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-white font-semibold">BrixBot Support</h3>
                  <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-red-500'}`} 
                       title={wsConnected ? 'Connected' : 'Disconnected'} />
                </div>
                <p className="text-gray-400 text-sm">We're here to help you!</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                <p>Start a conversation with our support team</p>
                <p className="text-sm mt-2">Type your message below to get started</p>
              </div>
            ) : (
              messages.map((message) => (
                <MessageDisplay
                  key={message.id}
                  message={message}
                  isOwnMessage={message.isOwn}
                  senderName={message.sender_name}
                  senderRole={message.sender_role}
                  currentUserRole={user.role}
                  hideTimestamp={true}
                  onDownloadFile={handleFileDownload}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - Always Visible */}
          <div className="p-4 border-t border-gray-700">
            {/* File Preview */}
            {selectedFile && (
              <div className="mb-3 p-3 bg-gray-800 rounded-lg border border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {selectedFile.content_type.startsWith('image/') ? (
                      <Image className="w-5 h-5 text-blue-400" />
                    ) : (
                      <FileText className="w-5 h-5 text-green-400" />
                    )}
                    <div>
                      <p className="text-white text-sm font-medium">{selectedFile.filename}</p>
                      <p className="text-gray-400 text-xs">{(selectedFile.file_size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={removeSelectedFile} className="text-gray-400 hover:text-white">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="text-gray-400 hover:text-white"
                title="Attach file"
              >
                <Paperclip className="w-4 h-4" />
              </Button>
              
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleCustomerKeyPress}
                placeholder={selectedFile ? "Add a caption (optional)" : "Type your message to BrixBot Support..."}
                className="flex-1 bg-gray-800 border-gray-600 text-white"
                disabled={uploadingFile}
              />
              
              <Button
                onClick={handleCustomerSendMessage}
                disabled={uploadingFile || (!newMessage.trim() && !selectedFile)}
                className="bg-violet-600 hover:bg-violet-700"
                title="Send message"
              >
                {uploadingFile ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf"
              className="hidden"
            />
          </div>
        </Card>
      </div>
    );
  }

  // Admin/Employee view - Two categories with filters
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 md:p-4">
      <Card className="w-full max-w-6xl h-[700px] bg-gray-900 border-violet-500/20 flex">
        
        {/* Sidebar */}
        <div className={`${showMobileSidebar ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-1/3 border-r border-gray-700`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-white text-xl font-semibold">Chat</h2>
                {/* WebSocket connection status */}
                <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-red-500'}`} 
                     title={wsConnected ? 'Connected' : 'Disconnected'} />
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                  className="md:hidden text-gray-400 hover:text-white"
                >
                  <MessageCircle className="w-4 h-4" />
                </Button>
                <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            
            {/* Categories */}
            <div className="flex space-x-2 mb-4">
              <Button
                variant={activeCategory === 'customers' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory('customers')}
                className="flex-1"
              >
                <Building2 className="w-4 h-4 mr-1" />
                Customers
              </Button>
              <Button
                variant={activeCategory === 'team' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory('team')}
                className="flex-1"
              >
                <Users className="w-4 h-4 mr-1" />
                Team
              </Button>
            </div>

            {/* Filters */}
            <div className="flex space-x-1 mb-4">
              <Button
                variant={filterType === 'all' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFilterType('all');
                }}
                className="flex-1 text-xs relative z-10"
              >
                All
              </Button>
              <Button
                variant={filterType === 'unread' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFilterType('unread');
                }}
                className="flex-1 text-xs relative z-10"
              >
                Unread
              </Button>
              <Button
                variant={filterType === 'no_reply' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFilterType('no_reply');
                }}
                className="flex-1 text-xs relative z-10"
              >
                No Reply
              </Button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search contacts..."
                className="pl-10 bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          {/* Contact List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {/* Existing rooms */}
                {getCurrentRooms().map((room) => (
                  <div
                    key={room.id}
                    onClick={async () => {
                      setSelectedChat({
                        id: room.id,
                        name: room.name,
                        type: room.room_type,
                        participants: room.participant_details
                      });
                      
                      // Load messages with correct room type
                      await loadRoomMessages(room.id, room.room_type);
                      
                      // Mark messages as read and refresh room data
                      try {
                        await chatAPI.markMessagesRead(room.id);
                        
                        // Refresh rooms to update unread counts
                        await loadRooms();
                        
                        // Clear unread count for this room locally
                        setUnreadCounts(prev => {
                          const updated = { ...prev };
                          delete updated[room.id];
                          return updated;
                        });
                        
                      } catch (readError) {
                        console.error('Error marking messages as read:', readError);
                      }
                      
                      if (window.innerWidth < 768) setShowMobileSidebar(false);
                    }}
                    className={`group p-3 rounded-lg cursor-pointer transition-colors border ${
                      selectedChat?.id === room.id 
                        ? 'bg-violet-600/20 border-violet-500/30' 
                        : 'hover:bg-gray-800 border-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        {/* Avatar or icon */}
                        <div className="relative">
                          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
                            {room.room_type === 'support' ? (
                              <Building2 className="w-5 h-5 text-white" />
                            ) : room.room_type === 'direct' ? (
                              <Users className="w-5 h-5 text-white" />
                            ) : (
                              <MessageCircle className="w-5 h-5 text-white" />
                            )}
                          </div>
                          {/* Online status indicator for direct chats */}
                          {room.room_type === 'direct' && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900"></div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Thread name - show participants for better email-like view */}
                          <div className="flex items-center space-x-1">
                            <p className="text-white font-medium truncate">
                              {room.room_type === 'direct' && room.participant_details ? (
                                // For direct chats, show the other participant's name
                                room.participant_details
                                  .filter(p => p.id !== user.user_id)
                                  .map(p => p.name)
                                  .join(', ') || 'Direct Chat'
                              ) : (
                                room.name
                              )}
                            </p>
                            
                            {/* Participant count for group chats */}
                            {room.participant_details && room.participant_details.length > 2 && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0.5">
                                {room.participant_details.length}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Show participant roles for context */}
                          {room.participant_details && room.participant_details.length > 0 && (
                            <div className="flex items-center space-x-1 mt-0.5">
                              {room.participant_details
                                .filter(p => p.id !== user.user_id)
                                .slice(0, 3)
                                .map((participant, index) => (
                                  <Badge
                                    key={participant.id}
                                    className={`text-xs px-1.5 py-0.5 ${
                                      participant.role === 'admin' ? 'bg-red-600/20 text-red-300' :
                                      participant.role === 'employee' ? 'bg-blue-600/20 text-blue-300' :
                                      participant.role === 'customer' ? 'bg-green-600/20 text-green-300' :
                                      'bg-purple-600/20 text-purple-300'
                                    }`}
                                  >
                                    {participant.role}
                                  </Badge>
                                ))
                              }
                              {room.participant_details.filter(p => p.id !== user.user_id).length > 3 && (
                                <span className="text-gray-400 text-xs">
                                  +{room.participant_details.filter(p => p.id !== user.user_id).length - 3}
                                </span>
                              )}
                            </div>
                          )}
                          
                          {/* Last message preview */}
                          {room.last_message && (
                            <p className="text-gray-400 text-sm truncate mt-1">
                              {room.last_message_sender === user.user_id ? 'You: ' : ''}
                              {room.last_message}
                            </p>
                          )}
                          
                          {/* Time and activity indicators */}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-gray-500 text-xs">
                              {room.last_activity ? 
                                new Date(room.last_activity).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 
                                'No activity'
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status indicators and actions */}
                      <div className="flex items-center space-x-2 ml-2">
                        <div className="flex flex-col items-end space-y-1">
                          {/* Unread count */}
                          {room.unread_count > 0 && (
                            <Badge className="bg-red-600 hover:bg-red-600 text-white text-xs min-w-[20px] h-5 flex items-center justify-center">
                              {room.unread_count > 99 ? '99+' : room.unread_count}
                            </Badge>
                          )}
                          
                          {/* No reply indicator */}
                          {!room.has_reply && room.last_message_sender !== user.user_id && (
                            <div className="w-2 h-2 bg-orange-500 rounded-full" title="Needs reply"></div>
                          )}
                        </div>
                        
                        {/* Delete button for admins */}
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteRoom(room);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-400 p-1 h-6 w-6 ml-1 relative z-20"
                            title="Delete chat"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Available contacts */}
                {getAllAvailableContacts().map((contact) => (
                  <div
                    key={contact.id}
                    onClick={() => startChat(contact)}
                    className="p-3 rounded-lg cursor-pointer hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
                        <UserCheck className="w-4 h-4 text-gray-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">
                          {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email}
                        </p>
                        <p className="text-gray-400 text-sm truncate">{contact.email}</p>
                        {contact.company_name && (
                          <p className="text-gray-500 text-xs truncate">{contact.company_name}</p>
                        )}
                      </div>
                      <Badge className={`text-xs ${
                        contact.role === 'admin' ? 'bg-red-600/20 text-red-300' :
                        contact.role === 'employee' ? 'bg-blue-600/20 text-blue-300' :
                        contact.role === 'customer' ? 'bg-green-600/20 text-green-300' :
                        'bg-purple-600/20 text-purple-300'
                      }`}>
                        {contact.role}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${showMobileSidebar ? 'hidden' : 'flex'} md:flex flex-col flex-1`}>
          {selectedChat ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-700">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowMobileSidebar(true)}
                      className="md:hidden text-gray-400 hover:text-white"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    
                    <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center">
                      {selectedChat.type === 'support' ? (
                        <Building2 className="w-5 h-5 text-white" />
                      ) : selectedChat.type === 'direct' ? (
                        <Users className="w-5 h-5 text-white" />
                      ) : (
                        <MessageCircle className="w-5 h-5 text-white" />
                      )}
                    </div>
                    
                    <div>
                      <h3 className="text-white font-semibold">
                        {selectedChat.type === 'direct' && selectedChat.participants ? (
                          // For direct chats, show the other participant's name
                          selectedChat.participants
                            .filter(p => p.id !== user.user_id)
                            .map(p => p.name)
                            .join(', ') || 'Direct Chat'
                        ) : (
                          selectedChat.name
                        )}
                      </h3>
                      
                      {/* Show participants info */}
                      <div className="flex items-center space-x-1">
                        <p className="text-gray-400 text-sm">
                          {selectedChat.type === 'support' ? 'Customer Support' : 
                           selectedChat.type === 'direct' ? 'Direct Chat' : 'Team Chat'}
                        </p>
                        
                        {selectedChat.participants && selectedChat.participants.length > 0 && (
                          <>
                            <span className="text-gray-500">•</span>
                            <div className="flex items-center space-x-1">
                              {selectedChat.participants
                                .filter(p => p.id !== user.user_id)
                                .slice(0, 2)
                                .map((participant, index) => (
                                  <Badge
                                    key={participant.id}
                                    className={`text-xs px-1.5 py-0.5 ${
                                      participant.role === 'admin' ? 'bg-red-600/20 text-red-300' :
                                      participant.role === 'employee' ? 'bg-blue-600/20 text-blue-300' :
                                      participant.role === 'customer' ? 'bg-green-600/20 text-green-300' :
                                      'bg-purple-600/20 text-purple-300'
                                    }`}
                                  >
                                    {participant.role}
                                  </Badge>
                                ))
                              }
                              {selectedChat.participants.filter(p => p.id !== user.user_id).length > 2 && (
                                <span className="text-gray-400 text-xs">
                                  +{selectedChat.participants.filter(p => p.id !== user.user_id).length - 2} more
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-400 py-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <MessageDisplay
                      key={message.id}
                      message={message}
                      isOwnMessage={message.isOwn}
                      senderName={message.sender_name}
                      senderRole={message.sender_role}
                      currentUserRole={user.role}
                      onDownloadFile={handleFileDownload}
                    />
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 border-t border-gray-700">
                {/* File Preview */}
                {selectedFile && (
                  <div className="mb-3 p-3 bg-gray-800 rounded-lg border border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {selectedFile.content_type.startsWith('image/') ? (
                          <Image className="w-5 h-5 text-blue-400" />
                        ) : (
                          <FileText className="w-5 h-5 text-green-400" />
                        )}
                        <div>
                          <p className="text-white text-sm font-medium">{selectedFile.filename}</p>
                          <p className="text-gray-400 text-xs">{(selectedFile.file_size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={removeSelectedFile} className="text-gray-400 hover:text-white">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="text-gray-400 hover:text-white"
                  >
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={selectedFile ? "Add a caption (optional)" : "Type your message..."}
                    className="flex-1 bg-gray-800 border-gray-600 text-white"
                    disabled={uploadingFile}
                  />
                  
                  <Button
                    onClick={sendMessage}
                    disabled={uploadingFile || (!newMessage.trim() && !selectedFile)}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    {uploadingFile ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.rtf"
                  className="hidden"
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
                <p>Choose a contact or existing conversation to start chatting</p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-60 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-gray-900 border-red-500/20">
            <CardContent className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-600/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Delete Chat</h3>
                  <p className="text-gray-400 text-sm">This action cannot be undone</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6">
                Are you sure you want to delete the chat "{roomToDelete?.name}"? 
                All messages in this chat will be permanently deleted.
              </p>
              
              <div className="flex space-x-3">
                <Button
                  variant="ghost"
                  onClick={cancelDeleteRoom}
                  className="flex-1 text-gray-400 hover:text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDeleteRoom}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Delete Chat
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SimpleChatSystem;